import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Reveal, { Eyebrow } from "./Reveal";

const STEPS = [
  { n: "01", title: "Pick a use case", body: "Collections, clinic, garage, pharmacy, restaurant — start with the one that costs you the most dialling." },
  { n: "02", title: "Configure the pack", body: "Agents, scripts, services, language rule — each pack is configured on the same platform core." },
  { n: "03", title: "Run it", body: "Inbound calls answered, outbound campaigns dispatched. Upload leads by CSV and dispatch at once." },
  { n: "04", title: "Get the results back", body: "Dispositions, transcripts, recordings — and an export in the same 16-column shape as your files." },
];

const CALL_STEPS = [
  "Lead or caller in — your list, as-is, or a customer calling you",
  "AI calls or answers — in Hindi, English or Bengali",
  "Identity checked first, where it matters",
  "The right conversation — booking, reminder, promise, or settlement",
  "Everything saved — disposition, transcript, recording, fields",
  "Results back — dashboard and export in your file format",
];

const HowItWorks = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [-40, 60]);

  return (
    <section id="how-it-works" data-testid="how-it-works-section" ref={ref} className="py-20 sm:py-28 bg-[#09090B] text-[#FAF9F6] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              <Eyebrow>How it works</Eyebrow>
              <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.08]">
                From list to results in four moves.
              </h2>
            </Reveal>
            <div className="mt-12 space-y-0">
              {STEPS.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.07}>
                  <div data-testid={`how-step-${i + 1}`} className="group flex gap-6 sm:gap-8 py-7 border-b border-white/10 first:border-t items-start">
                    <span className="font-mono2 text-sm text-[#2563EB] pt-1 shrink-0">{s.n}</span>
                    <div>
                      <h3 className="font-display font-bold text-xl sm:text-2xl transition-colors duration-300 group-hover:text-[#60A5FA]">{s.title}</h3>
                      <p className="text-zinc-400 mt-2 text-sm sm:text-base leading-relaxed max-w-lg">{s.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.15} className="lg:sticky lg:top-28">
              <div className="relative rounded-2xl overflow-hidden border border-white/10">
                <motion.img
                  style={{ y: yImg }}
                  src="https://images.unsplash.com/photo-1530541263627-4314dd7a44c2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                  alt="A modern operations team at work while the AI handles the calling"
                  className="w-full h-56 sm:h-64 object-cover scale-110 saturate-[0.35]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/30 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-6">
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.28em] text-[#60A5FA]">How a call works</p>
                </div>
              </div>
              <ol className="mt-6 space-y-3.5">
                {CALL_STEPS.map((c, i) => (
                  <li key={i} className="flex gap-3.5 text-sm text-zinc-300 leading-relaxed" data-testid={`call-step-${i + 1}`}>
                    <span className="font-mono2 text-xs text-zinc-500 pt-0.5 shrink-0">{i + 1}.</span>
                    {c}
                  </li>
                ))}
              </ol>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
